import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid, Modal, RadioProps } from 'semantic-ui-react';
import { PrinterConfigDto, UpsertPrintoutConfigDto } from 'ts-types/api.types';
import CheckBox from 'components/final-form/CheckBox';
import axios, { CancelTokenSource } from 'axios';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import { PrintOrientation } from 'ts-types/api.enums';
import { editPrintoutConfig, savePrintoutConfig } from 'services/printoutConfigServices';
import Radio from 'components/final-form/Radio';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import Select from 'components/final-form/Select';
import styled from 'styled-components';
import CodeEditor from '@uiw/react-textarea-code-editor';


const StyledEditorWrapper = styled.div`
  resize: vertical;
  overflow: auto;
  border: 1px solid rgba(34, 36, 38, .15);
  border-radius: .28571429rem;
  height: 90px;

  .w-tc-editor {
    min-height: 100%;
  }
`;

const StyledBodyEditorWrapper = styled(StyledEditorWrapper)`
  height: 250px;
`;

interface Props extends MessageSourceProps {
  printoutConfigId?: number,
  printoutConfig: UpsertPrintoutConfigDto,
  printers: Array<PrinterConfigDto>,
  reportKeyName: string,
  modalOpen: boolean,
  onCloseModal: () => void,
  onSave: () => void,
  mandate: DropdownOption,
}

interface State {
  orientations: Array<RadioProps>,
  printerOptions: Array<DropdownOption>,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertPrintoutConfigForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {getMessage, printers} = props;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      orientations: generateRadioButtonOptionsFromEnum(Object.values(PrintOrientation), "orientation", getMessage),
      printerOptions: printers.length > 0 ? mapToDropdownOptionArray(printers, "name") : [],
      cancelTokenSource
    }
  };

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleSubmit = async (values: Partial<UpsertPrintoutConfigDto>) => {
    const {printoutConfigId, getMessage, onSave} = this.props;

    const handleError = () => {
      this.setErrorMessage(getMessage("error.general"))
    };

    let modifiedValues = {...values};
    if (modifiedValues.mandateId === -1) {
      modifiedValues.mandateId = undefined
    }

    if (printoutConfigId) {
      await editPrintoutConfig(printoutConfigId, modifiedValues)
          .then(onSave)
          .catch(handleError)
    } else {
      await savePrintoutConfig(modifiedValues)
          .then(onSave)
          .catch(handleError)
    }
  };

  render(): React.ReactNode {

    const {printoutConfig, modalOpen, onCloseModal, getMessage} = this.props;

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("printoutConfig.config")}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                initialValues={printoutConfig}
                subscription={{pristine: true, submitting: true}}
                render={this.renderForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {mandate, printoutConfig, getMessage, onCloseModal} = this.props;
    const {orientations, errorMessage, printerOptions} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <PraxFormScrollableContainer height="700px">
                <PraxFormGrid>
                  {errorMessage
                  && (
                      <Grid.Row>
                        <Grid.Column>
                          <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                            {errorMessage}
                          </StyledErrorMessage>
                        </Grid.Column>
                      </Grid.Row>
                  )
                  }
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("mandate")}:
                    </Grid.Column>
                    <Grid.Column width={5}>
                      {mandate.text}
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Field
                          name="preview"
                          component={CheckBox}
                          label={getMessage("printoutConfig.preview")}
                      />
                    </Grid.Column>
                    <Grid.Column width={3} verticalAlign="middle">
                      <Field
                          name="orientation"
                          component={Radio}
                          radioDefinition={orientations[1]}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2}>
                      {getMessage("printoutConfig.reportKey")}:
                    </Grid.Column>
                    <Grid.Column width={5}>
                      {printoutConfig.reportKey}
                    </Grid.Column>
                    <Grid.Column width={3}>
                      <Field
                          name="printDialog"
                          component={CheckBox}
                          label={getMessage("printoutConfig.printDialog")}
                      />
                    </Grid.Column>
                    <Grid.Column width={3} verticalAlign="middle">
                      <Field
                          name="orientation"
                          component={Radio}
                          radioDefinition={orientations[0]}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={2} verticalAlign="middle">
                      {getMessage("printoutConfig.printer")}:
                    </Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          name="printerConfId"
                          component={Select}
                          options={printerOptions}
                          clearable
                          fluid
                      />
                    </Grid.Column>
                    <Grid.Column width={9}>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      {getMessage("printoutConfig.header")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Field name="header">
                        {(props: { input: { name: any; value: any; onChange: any; }; }) => (
                            <StyledEditorWrapper>
                              <CodeEditor
                                  value={props.input.value}
                                  language="xml"
                                  onChange={props.input.onChange}
                                  padding={5}
                                  style={{
                                    fontSize: 12,
                                    backgroundColor: "#f5f5f5",
                                    fontFamily: 'ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace'
                                  }}
                              />
                            </StyledEditorWrapper>
                        )}
                      </Field>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      {getMessage("printoutConfig.body")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Field name="template">
                        {(props: { input: { name: any; value: any; onChange: any; }; }) => (
                            <StyledBodyEditorWrapper>
                              <CodeEditor
                                  value={props.input.value}
                                  language="xml"
                                  onChange={props.input.onChange}
                                  padding={5}
                                  style={{
                                    fontSize: 12,
                                    backgroundColor: "#f5f5f5",
                                    fontFamily: 'ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace'
                                  }}
                              />
                            </StyledBodyEditorWrapper>
                        )}
                      </Field>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      {getMessage("printoutConfig.footer")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Field name="footer">
                        {(props: { input: { name: any; value: any; onChange: any; }; }) => (
                            <StyledEditorWrapper>
                              <CodeEditor
                                  value={props.input.value}
                                  language="xml"
                                  onChange={props.input.onChange}
                                  padding={5}
                                  style={{
                                    fontSize: 12,
                                    backgroundColor: "#f5f5f5",
                                    fontFamily: 'ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace'
                                  }}
                              />
                            </StyledEditorWrapper>
                        )}
                      </Field>
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
              </PraxFormScrollableContainer>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>
                {getMessage("button.save")}
              </Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCloseModal}>{getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertPrintoutConfigForm);
